"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tokenizer = exports.TokenType = void 0;
var TokenType;
(function (TokenType) {
    TokenType["HEADER"] = "HEADER";
    TokenType["SECTION"] = "SECTION";
    TokenType["CHARACTER"] = "CHARACTER";
    TokenType["PLAYER"] = "PLAYER";
    TokenType["SCENE"] = "SCENE";
    TokenType["ITEM"] = "ITEM";
    TokenType["DESCRIPTION"] = "DESCRIPTION";
    TokenType["NAME"] = "NAME";
    TokenType["STARTING_SCENE"] = "STARTING_SCENE";
    TokenType["STARTING_CHARACTER"] = "STARTING_CHARACTER";
    TokenType["PORTRAIT"] = "PORTRAIT";
    TokenType["TITLE_IMAGE"] = "TITLE_IMAGE";
    TokenType["TAGS"] = "TAGS";
    TokenType["TICK_INTERVAL_MS"] = "TICK_INTERVAL_MS";
    TokenType["HIDE_HEADER_FOR_GUESTS"] = "HIDE_HEADER_FOR_GUESTS";
    /** Library: true — entity library home adventure */
    TokenType["LIBRARY"] = "LIBRARY";
    TokenType["IS_NPC"] = "IS_NPC";
    TokenType["NPC_UPDATE"] = "NPC_UPDATE";
    /** Kind: fixed | open (characters and player groups) */
    TokenType["KIND"] = "KIND";
    /** Min: (open player group) */
    TokenType["MIN"] = "MIN";
    /** Max: (open player group) */
    TokenType["MAX"] = "MAX";
    TokenType["ICON"] = "ICON";
    TokenType["LOCATION_IMAGE"] = "LOCATION_IMAGE";
    TokenType["LOCATION"] = "LOCATION";
    TokenType["STARTING_INVENTORY"] = "STARTING_INVENTORY";
    TokenType["STYLE"] = "STYLE";
    TokenType["AUTO_EFFECTS"] = "AUTO_EFFECTS";
    TokenType["OPTION"] = "OPTION";
    TokenType["TARGET"] = "TARGET";
    TokenType["CONDITIONS"] = "CONDITIONS";
    TokenType["EFFECTS"] = "EFFECTS";
    TokenType["WAIT_MESSAGE"] = "WAIT_MESSAGE";
    TokenType["AUTO_TRANSITION"] = "AUTO_TRANSITION";
    TokenType["BACK_OPTION"] = "BACK_OPTION";
    TokenType["TEXT"] = "TEXT";
    TokenType["SCRIPT_STRING"] = "SCRIPT_STRING";
    TokenType["VARIABLES"] = "VARIABLES";
    TokenType["VARIABLE_DECLARATION"] = "VARIABLE_DECLARATION";
    /** ### under ## Activity Types */
    TokenType["ACTIVITY_TYPE_HEADING"] = "ACTIVITY_TYPE_HEADING";
    /** ### under ## Activities */
    TokenType["ACTIVITY_DEF"] = "ACTIVITY_DEF";
    /** ### under ## Entity Types */
    TokenType["ENTITY_TYPE_HEADING"] = "ENTITY_TYPE_HEADING";
    /** Type: (activity definition — references Activity Types) */
    TokenType["ACTIVITY_FIELD_TYPE"] = "ACTIVITY_FIELD_TYPE";
    TokenType["ACTIVITY_PARENT"] = "ACTIVITY_PARENT";
    TokenType["REQUIRES_COMPLETED"] = "REQUIRES_COMPLETED";
    TokenType["REQUIRES_ACTIVE"] = "REQUIRES_ACTIVE";
    TokenType["ELIGIBLE"] = "ELIGIBLE";
    TokenType["ACTIVITY_START_SCENE"] = "ACTIVITY_START_SCENE";
    TokenType["ACTIVITY_SCENE_PREFIX"] = "ACTIVITY_SCENE_PREFIX";
    TokenType["LIST_ORDER"] = "LIST_ORDER";
    TokenType["ACTIVITY_REQUIRES_SCRIPT"] = "ACTIVITY_REQUIRES_SCRIPT";
    TokenType["ACTIVITY_INITIALISE_SCRIPT"] = "ACTIVITY_INITIALISE_SCRIPT";
    TokenType["HIDE_WHEN_COMPLETED"] = "HIDE_WHEN_COMPLETED";
    TokenType["ALLOWED_PARENTS"] = "ALLOWED_PARENTS";
    TokenType["ALLOWED_CHILDREN"] = "ALLOWED_CHILDREN";
    TokenType["ERROR"] = "ERROR";
    TokenType["NEWLINE"] = "NEWLINE";
    TokenType["EOF"] = "EOF";
})(TokenType || (exports.TokenType = TokenType = {}));
class Tokenizer {
    constructor(input) {
        this.position = 0;
        this.line = 1;
        this.column = 1;
        this.tokens = [];
        this.isFirstLine = true;
        this.currentSection = '';
        // Normalize line endings to Unix style (\n) to handle both Windows (\r\n) and Unix (\n) line endings
        this.input = input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    }
    tokenize() {
        this.tokens = [];
        this.position = 0;
        this.line = 1;
        this.column = 1;
        this.isFirstLine = true;
        // Normalize line endings to handle both Windows (\r\n) and Unix (\n) formats
        const normalizedInput = this.input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = normalizedInput.split('\n');
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            this.line = i + 1;
            this.column = 1;
            if (line.trim() === '') {
                this.addToken(TokenType.NEWLINE, '\n');
                continue;
            }
            // Check for different line types
            if (line.startsWith('###')) {
                // Character, Scene, or Item definition - depends on current section
                const name = line.substring(3).trim();
                if (this.currentSection.toLowerCase() === 'characters') {
                    this.addToken(TokenType.CHARACTER, name);
                }
                else if (this.currentSection.toLowerCase() === 'players') {
                    this.addToken(TokenType.PLAYER, name);
                }
                else if (this.currentSection.toLowerCase() === 'scenes') {
                    this.addToken(TokenType.SCENE, name);
                }
                else if (this.currentSection.toLowerCase() === 'items') {
                    this.addToken(TokenType.ITEM, name);
                }
                else if (this.currentSection.toLowerCase() === 'locations') {
                    this.addToken(TokenType.ITEM, name); // Reuse ITEM token for locations
                }
                else if (this.currentSection.toLowerCase() === 'activity types') {
                    this.addToken(TokenType.ACTIVITY_TYPE_HEADING, name);
                }
                else if (this.currentSection.toLowerCase() === 'activities') {
                    this.addToken(TokenType.ACTIVITY_DEF, name);
                }
                else if (this.currentSection.toLowerCase() === 'entity types') {
                    this.addToken(TokenType.ENTITY_TYPE_HEADING, name);
                }
                else {
                    // Default to CHARACTER for backwards compatibility
                    this.addToken(TokenType.CHARACTER, name);
                }
            }
            else if (line.startsWith('##')) {
                // Check if this is a real section header (only at top level, not in descriptions)
                const name = line.substring(2).trim();
                // Only treat as section if it's one of the known sections
                if (['Characters', 'Character Variables', 'Shared Variables', 'Players', 'Scenes', 'Items', 'Locations', 'Activity Types', 'Activities', 'Entity Types', 'Stats', 'Sub-Adventures', 'sub-adventures', 'Variables', 'Scripts'].includes(name)) {
                    this.currentSection = name; // Track current section
                    this.addToken(TokenType.SECTION, name);
                }
                else {
                    // This is a rich text heading within a description
                    this.addToken(TokenType.TEXT, line.trim());
                }
            }
            else if (line.startsWith('#')) {
                if (this.isFirstLine) {
                    // First line starting with # is the adventure title
                    const title = line.substring(1).trim();
                    this.addToken(TokenType.HEADER, title);
                    this.isFirstLine = false;
                }
                else {
                    // Other lines starting with # are part of rich text descriptions
                    this.addToken(TokenType.TEXT, line.trim());
                }
            }
            else if (line.startsWith('Description:')) {
                this.addToken(TokenType.DESCRIPTION, 'Description:');
                // Check for opening delimiter
                const restOfLine = line.substring(12).trim();
                if (restOfLine.startsWith('"""')) {
                    // Simple approach: find the next """ and take everything in between
                    const result = this.extractQuotedContentFromCurrentLine(lines, i);
                    if (result.text !== null) {
                        this.addToken(TokenType.TEXT, result.text);
                        i = result.endIndex; // Skip to closing delimiter
                    }
                    else {
                        // Error: Missing closing delimiter
                        this.addToken(TokenType.TEXT, ''); // Empty description
                    }
                }
                else if (restOfLine === '') {
                    // Multi-line format - next line should have opening """
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === '"""') {
                        // Read until closing """
                        const result = this.extractMultiLineQuotedContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.TEXT, result.text);
                            i = result.endIndex; // Skip to closing delimiter
                        }
                        else {
                            // Error: Missing closing delimiter
                            this.addToken(TokenType.TEXT, ''); // Empty description
                        }
                    }
                    else {
                        // Error: Missing opening delimiter - reject old format
                        this.addToken(TokenType.ERROR, `Description at line ${i + 1} must use triple-quote delimiters. Old format is no longer supported. Use: Description: """content"""`);
                    }
                }
                else {
                    // Error: Unexpected content after Description: - treat as error
                    this.addToken(TokenType.TEXT, ''); // Empty description
                }
            }
            else if (line.startsWith('Starting Scene:')) {
                this.addToken(TokenType.STARTING_SCENE, 'Starting Scene:');
                const scene = line.substring(15).trim();
                if (scene) {
                    this.addToken(TokenType.TEXT, scene);
                }
            }
            else if (line.startsWith('Starting Character:')) {
                this.addToken(TokenType.STARTING_CHARACTER, 'Starting Character:');
                const character = line.substring(19).trim();
                if (character) {
                    this.addToken(TokenType.TEXT, character);
                }
            }
            else if (line.startsWith('Portrait:')) {
                this.addToken(TokenType.PORTRAIT, 'Portrait:');
                const portraitId = line.substring(9).trim();
                if (portraitId) {
                    this.addToken(TokenType.TEXT, portraitId);
                }
            }
            else if (line.startsWith('TitleImage:')) {
                this.addToken(TokenType.TITLE_IMAGE, 'TitleImage:');
                const titleImageId = line.substring(11).trim();
                if (titleImageId) {
                    this.addToken(TokenType.TEXT, titleImageId);
                }
            }
            else if (line.startsWith('Tags:')) {
                this.addToken(TokenType.TAGS, 'Tags:');
                const tagsRaw = line.substring(5).trim();
                if (tagsRaw) {
                    this.addToken(TokenType.TEXT, tagsRaw);
                }
            }
            else if (line.startsWith('TickIntervalMs:')) {
                this.addToken(TokenType.TICK_INTERVAL_MS, 'TickIntervalMs:');
                const raw = line.substring(15).trim();
                if (raw) {
                    this.addToken(TokenType.TEXT, raw);
                }
            }
            else if (line.startsWith('HideHeaderForGuests:')) {
                this.addToken(TokenType.HIDE_HEADER_FOR_GUESTS, 'HideHeaderForGuests:');
                const raw = line.substring(20).trim();
                if (raw) {
                    this.addToken(TokenType.TEXT, raw);
                }
            }
            else if (line.startsWith('Library:')) {
                this.addToken(TokenType.LIBRARY, 'Library:');
                const raw = line.substring(8).trim();
                if (raw) {
                    this.addToken(TokenType.TEXT, raw);
                }
            }
            else if (line.startsWith('IsNpc:')) {
                this.addToken(TokenType.IS_NPC, 'IsNpc:');
                const v = line.substring(7).trim();
                if (v) {
                    this.addToken(TokenType.TEXT, v);
                }
            }
            else if (line.startsWith('Kind:')) {
                this.addToken(TokenType.KIND, 'Kind:');
                const v = line.substring(5).trim();
                if (v) {
                    this.addToken(TokenType.TEXT, v);
                }
            }
            else if (line.startsWith('Min:')) {
                this.addToken(TokenType.MIN, 'Min:');
                const v = line.substring(4).trim();
                if (v) {
                    this.addToken(TokenType.TEXT, v);
                }
            }
            else if (line.startsWith('Max:')) {
                this.addToken(TokenType.MAX, 'Max:');
                const v = line.substring(4).trim();
                if (v) {
                    this.addToken(TokenType.TEXT, v);
                }
            }
            else if (line.startsWith('Update:')) {
                this.addToken(TokenType.NPC_UPDATE, 'Update:');
                const restOfLine = line.substring(7).trim();
                if (restOfLine.startsWith("'''")) {
                    const script = this.extractSingleLineScriptContent(restOfLine, i);
                    if (script !== null) {
                        this.addToken(TokenType.SCRIPT_STRING, script);
                    }
                    else {
                        const result = this.extractInlineMultiLineScriptContent(lines, i, restOfLine);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                }
                else if (restOfLine === '') {
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === "'''") {
                        const result = this.extractMultiLineScriptContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                    else {
                        this.addToken(TokenType.ERROR, `Update at line ${i + 1} must use script format with triple single quotes. Use: Update: '''script'''`);
                    }
                }
                else {
                    this.addToken(TokenType.TEXT, restOfLine);
                }
            }
            else if (line.startsWith('Icon:')) {
                this.addToken(TokenType.ICON, 'Icon:');
                const iconId = line.substring(5).trim();
                if (iconId) {
                    this.addToken(TokenType.TEXT, iconId);
                }
            }
            else if (line.startsWith('LocationImage:')) {
                this.addToken(TokenType.LOCATION_IMAGE, 'LocationImage:');
                const locationImageId = line.substring(14).trim();
                if (locationImageId) {
                    this.addToken(TokenType.TEXT, locationImageId);
                }
            }
            else if (line.startsWith('Location:')) {
                this.addToken(TokenType.LOCATION, 'Location:');
                const locationTag = line.substring(9).trim();
                if (locationTag) {
                    this.addToken(TokenType.TEXT, locationTag);
                }
            }
            else if (line.startsWith('Starting Inventory:')) {
                this.addToken(TokenType.STARTING_INVENTORY, 'Starting Inventory:');
                const inventory = line.substring(19).trim();
                if (inventory) {
                    this.addToken(TokenType.TEXT, inventory);
                }
            }
            else if (line.startsWith('Requires Completed:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.REQUIRES_COMPLETED, 'Requires Completed:');
                const rest = line.substring('Requires Completed:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Requires Active:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.REQUIRES_ACTIVE, 'Requires Active:');
                const rest = line.substring('Requires Active:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Eligible Characters:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.ELIGIBLE, 'Eligible Characters:');
                const rest = line.substring('Eligible Characters:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Eligible:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.ELIGIBLE, 'Eligible:');
                const rest = line.substring('Eligible:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Start Scene:') && (this.currentSection.toLowerCase() === 'activities' || this.currentSection.toLowerCase() === 'entity types')) {
                this.addToken(TokenType.ACTIVITY_START_SCENE, 'Start Scene:');
                const rest = line.substring('Start Scene:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Scene Prefix:') && (this.currentSection.toLowerCase() === 'activities' || this.currentSection.toLowerCase() === 'entity types')) {
                this.addToken(TokenType.ACTIVITY_SCENE_PREFIX, 'Scene Prefix:');
                const rest = line.substring('Scene Prefix:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Allowed Parents:') && this.currentSection.toLowerCase() === 'entity types') {
                this.addToken(TokenType.ALLOWED_PARENTS, 'Allowed Parents:');
                const rest = line.substring('Allowed Parents:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Allowed Children:') && this.currentSection.toLowerCase() === 'entity types') {
                this.addToken(TokenType.ALLOWED_CHILDREN, 'Allowed Children:');
                const rest = line.substring('Allowed Children:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('List Order:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.LIST_ORDER, 'List Order:');
                const rest = line.substring('List Order:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Hide When Completed:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.HIDE_WHEN_COMPLETED, 'Hide When Completed:');
                const rest = line.substring('Hide When Completed:'.length).trim();
                if (rest) {
                    this.addToken(TokenType.TEXT, rest);
                }
            }
            else if (line.startsWith('Requires:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.ACTIVITY_REQUIRES_SCRIPT, 'Requires:');
                const restOfLine = line.substring('Requires:'.length).trim();
                if (restOfLine.startsWith("'''")) {
                    const script = this.extractSingleLineScriptContent(restOfLine, i);
                    if (script !== null) {
                        this.addToken(TokenType.SCRIPT_STRING, script);
                    }
                    else {
                        const result = this.extractInlineMultiLineScriptContent(lines, i, restOfLine);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                }
                else if (restOfLine === '') {
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === "'''") {
                        const result = this.extractMultiLineScriptContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                    else {
                        this.addToken(TokenType.ERROR, `Requires at line ${i + 1} must use script format with triple single quotes. Use: Requires: '''script'''`);
                    }
                }
                else {
                    this.addToken(TokenType.TEXT, restOfLine);
                }
            }
            else if (line.startsWith('Initialise:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.ACTIVITY_INITIALISE_SCRIPT, 'Initialise:');
                const restOfLine = line.substring('Initialise:'.length).trim();
                if (restOfLine.startsWith("'''")) {
                    const script = this.extractSingleLineScriptContent(restOfLine, i);
                    if (script !== null) {
                        this.addToken(TokenType.SCRIPT_STRING, script);
                    }
                    else {
                        const result = this.extractInlineMultiLineScriptContent(lines, i, restOfLine);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                }
                else if (restOfLine === '') {
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === "'''") {
                        const result = this.extractMultiLineScriptContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                    else {
                        this.addToken(TokenType.ERROR, `Initialise at line ${i + 1} must use script format with triple single quotes. Use: Initialise: '''script'''`);
                    }
                }
                else {
                    this.addToken(TokenType.TEXT, restOfLine);
                }
            }
            else if (line.startsWith('Type:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.ACTIVITY_FIELD_TYPE, 'Type:');
                const typeId = line.substring(5).trim();
                if (typeId) {
                    this.addToken(TokenType.TEXT, typeId);
                }
            }
            else if (line.startsWith('Parent:') && this.currentSection.toLowerCase() === 'activities') {
                this.addToken(TokenType.ACTIVITY_PARENT, 'Parent:');
                const parentId = line.substring(7).trim();
                if (parentId) {
                    this.addToken(TokenType.TEXT, parentId);
                }
            }
            else if (line.startsWith('Style:')) {
                this.addToken(TokenType.STYLE, 'Style:');
                // Check for inline style text
                const restOfLine = line.substring(6).trim();
                if (restOfLine) {
                    // Single-line format: Style: background_gradient: #ff0000, #00ff00
                    this.addToken(TokenType.TEXT, restOfLine);
                }
                else {
                    // Multi-line format - read style properties from subsequent lines
                    let styleText = '';
                    let j = i + 1;
                    while (j < lines.length && !this.isKeywordLine(lines[j])) {
                        styleText += lines[j] + '\n';
                        j++;
                    }
                    if (styleText.trim()) {
                        this.addToken(TokenType.TEXT, styleText.trim());
                    }
                    i = j - 1; // Adjust loop index
                }
            }
            else if (line.startsWith('Name:')) {
                this.addToken(TokenType.NAME, 'Name:');
                const name = line.substring(5).trim();
                if (name) {
                    this.addToken(TokenType.TEXT, name);
                }
            }
            else if (line.startsWith('Auto Effects:')) {
                this.addToken(TokenType.AUTO_EFFECTS, 'Auto Effects:');
                // Check for script delimiter
                const restOfLine = line.substring(13).trim();
                if (restOfLine.startsWith("'''")) {
                    // Try single-line script format first: Auto Effects: '''script'''
                    const script = this.extractSingleLineScriptContent(restOfLine, i);
                    if (script !== null) {
                        this.addToken(TokenType.SCRIPT_STRING, script);
                    }
                    else {
                        // Multi-line script starting on same line: Auto Effects: '''script
                        const result = this.extractInlineMultiLineScriptContent(lines, i, restOfLine);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                }
                else if (restOfLine === '') {
                    // Multi-line format - check if next line has script delimiter
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === "'''") {
                        // Multi-line script format
                        const result = this.extractMultiLineScriptContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex; // Skip to closing delimiter
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                    else {
                        // Error: Legacy format no longer supported
                        this.addToken(TokenType.ERROR, `Auto Effects at line ${i + 1} must use script format with triple single quotes. Old format is no longer supported. Use: Auto Effects: '''script'''`);
                    }
                }
                else {
                    // Inline text (legacy format or error)
                    this.addToken(TokenType.TEXT, restOfLine);
                }
            }
            else if (line.startsWith('Option:')) {
                this.addToken(TokenType.OPTION, 'Option:');
                // Check for opening delimiter
                const restOfLine = line.substring(7).trim();
                if (restOfLine.startsWith('"""')) {
                    // Single-line format: Option: """content"""
                    const content = this.extractSingleLineQuotedContent(restOfLine, i);
                    if (content !== null) {
                        this.addToken(TokenType.TEXT, content);
                    }
                }
                else if (restOfLine === '') {
                    // Multi-line format - next line should have opening """
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === '"""') {
                        // Read until closing """
                        const result = this.extractMultiLineQuotedContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.TEXT, result.text);
                            i = result.endIndex; // Skip to closing delimiter
                        }
                    }
                }
            }
            else if (line.startsWith('Target:')) {
                this.addToken(TokenType.TARGET, 'Target:');
                // Check for script delimiter
                const restOfLine = line.substring(7).trim();
                if (restOfLine.startsWith("'''")) {
                    // Single-line script format: Target: '''script'''
                    const script = this.extractSingleLineScriptContent(restOfLine, i);
                    if (script !== null) {
                        // Store with delimiters for runtime detection
                        this.addToken(TokenType.SCRIPT_STRING, "'''" + script + "'''");
                    }
                    else {
                        // Multi-line script starting on same line
                        const result = this.extractInlineMultiLineScriptContent(lines, i, restOfLine);
                        if (result.text !== null) {
                            // Store with delimiters for runtime detection
                            this.addToken(TokenType.SCRIPT_STRING, "'''" + result.text + "'''");
                            i = result.endIndex;
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                }
                else if (restOfLine === '') {
                    // Multi-line format - check if next line has script delimiter
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === "'''") {
                        // Multi-line script format
                        const result = this.extractMultiLineScriptContent(lines, j + 1);
                        if (result.text !== null) {
                            // Store with delimiters for runtime detection
                            this.addToken(TokenType.SCRIPT_STRING, "'''" + result.text + "'''");
                            i = result.endIndex; // Skip to closing delimiter
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                    else {
                        // Empty target - will be caught by parser validation
                        this.addToken(TokenType.TEXT, '');
                    }
                }
                else {
                    // Static target (text)
                    this.addToken(TokenType.TEXT, restOfLine);
                }
            }
            else if (line.startsWith('Condition:') || line.startsWith('Conditions:')) {
                // Accept both "Condition:" (singular) and "Conditions:" (plural) for option conditions
                const prefix = line.startsWith('Conditions:') ? 'Conditions:' : 'Condition:';
                this.addToken(TokenType.CONDITIONS, prefix);
                // Check for script delimiter
                const restOfLine = line.substring(prefix.length).trim();
                if (restOfLine.startsWith("'''")) {
                    // Single-line script format: Condition(s): '''script'''
                    const script = this.extractSingleLineScriptContent(restOfLine, i);
                    if (script !== null) {
                        this.addToken(TokenType.SCRIPT_STRING, script);
                    }
                    else {
                        // Multi-line script starting on same line
                        const result = this.extractInlineMultiLineScriptContent(lines, i, restOfLine);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                }
                else if (restOfLine === '') {
                    // Multi-line format - check if next line has script delimiter
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === "'''") {
                        // Multi-line script format
                        const result = this.extractMultiLineScriptContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex; // Skip to closing delimiter
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                    else {
                        // Error: Legacy format no longer supported
                        this.addToken(TokenType.ERROR, `Conditions at line ${i + 1} must use script format with triple single quotes. Old format is no longer supported. Use: Conditions: '''script'''`);
                    }
                }
                else {
                    // Inline text (legacy format or error)
                    this.addToken(TokenType.TEXT, restOfLine);
                }
            }
            else if (line.startsWith('Effects:')) {
                this.addToken(TokenType.EFFECTS, 'Effects:');
                // Check for script delimiter
                const restOfLine = line.substring(8).trim();
                if (restOfLine.startsWith("'''")) {
                    // Single-line script format: Effects: '''script'''
                    const script = this.extractSingleLineScriptContent(restOfLine, i);
                    if (script !== null) {
                        this.addToken(TokenType.SCRIPT_STRING, script);
                    }
                    else {
                        // Multi-line script starting on same line
                        const result = this.extractInlineMultiLineScriptContent(lines, i, restOfLine);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex;
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                }
                else if (restOfLine === '') {
                    // Multi-line format - check if next line has script delimiter
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === "'''") {
                        // Multi-line script format
                        const result = this.extractMultiLineScriptContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.SCRIPT_STRING, result.text);
                            i = result.endIndex; // Skip to closing delimiter
                        }
                        else {
                            // Script extraction failed - add error token
                            this.addToken(TokenType.ERROR, `Unclosed script block starting at line ${i + 1}. Script blocks must be closed with '''.`);
                        }
                    }
                    else {
                        // Error: Legacy format no longer supported
                        this.addToken(TokenType.ERROR, `Effects at line ${i + 1} must use script format with triple single quotes. Old format is no longer supported. Use: Effects: '''script'''`);
                    }
                }
                else {
                    // Inline text (legacy format or error)
                    this.addToken(TokenType.TEXT, restOfLine);
                }
            }
            else if (line.startsWith('Wait Message:')) {
                this.addToken(TokenType.WAIT_MESSAGE, 'Wait Message:');
                // Check for opening delimiter
                const restOfLine = line.substring(13).trim();
                if (restOfLine.startsWith('"""')) {
                    // Single-line format: Wait Message: """content"""
                    const content = this.extractSingleLineQuotedContent(restOfLine, i);
                    if (content !== null) {
                        this.addToken(TokenType.TEXT, content);
                    }
                }
                else if (restOfLine === '') {
                    // Multi-line format - next line should have opening """
                    let j = i + 1;
                    if (j < lines.length && lines[j].trim() === '"""') {
                        // Read until closing """
                        const result = this.extractMultiLineQuotedContent(lines, j + 1);
                        if (result.text !== null) {
                            this.addToken(TokenType.TEXT, result.text);
                            i = result.endIndex; // Skip to closing delimiter
                        }
                    }
                }
            }
            else if (line.startsWith('Auto-Transition:')) {
                this.addToken(TokenType.AUTO_TRANSITION, 'Auto-Transition:');
                const autoTransitionValue = line.substring(16).trim().toLowerCase();
                if (autoTransitionValue === 'true' || autoTransitionValue === 'false') {
                    this.addToken(TokenType.TEXT, autoTransitionValue);
                }
                else {
                    // Invalid value - treat as error
                    this.addToken(TokenType.ERROR, `Auto-Transition at line ${i + 1} must be 'true' or 'false'. Got: '${autoTransitionValue}'`);
                }
            }
            else if (line.startsWith('Back Option:')) {
                this.addToken(TokenType.BACK_OPTION, 'Back Option:');
                const backOptionValue = line.substring(12).trim().toLowerCase();
                if (backOptionValue === 'true' || backOptionValue === 'false' || backOptionValue === '1' || backOptionValue === '0') {
                    this.addToken(TokenType.TEXT, backOptionValue);
                }
                else {
                    this.addToken(TokenType.ERROR, `Back Option at line ${i + 1} must be 'true' or 'false'. Got: '${backOptionValue}'`);
                }
            }
            else if (line.startsWith('Variables:')) {
                this.addToken(TokenType.VARIABLES, 'Variables:');
                // Variables block starts, continue to next line
            }
            else if (this.isVariableDeclaration(line)) {
                this.addToken(TokenType.VARIABLE_DECLARATION, line.trim());
            }
            else {
                // Regular text line
                this.addToken(TokenType.TEXT, line.trim());
            }
            this.addToken(TokenType.NEWLINE, '\n');
        }
        this.addToken(TokenType.EOF, '');
        return this.tokens;
    }
    isKeywordLine(line) {
        const trimmed = line.trim();
        return trimmed.startsWith('###') ||
            (trimmed.startsWith('##') && ['Characters', 'Character Variables', 'Players', 'Scenes', 'Items', 'Locations', 'Activity Types', 'Activities', 'Entity Types', 'Stats', 'Sub-Adventures', 'sub-adventures', 'Variables', 'Shared Variables'].includes(trimmed.substring(2).trim())) ||
            trimmed.startsWith('Description:') ||
            trimmed.startsWith('Name:') ||
            trimmed.startsWith('Starting Scene:') ||
            trimmed.startsWith('Starting Character:') ||
            trimmed.startsWith('Portrait:') ||
            trimmed.startsWith('TitleImage:') ||
            trimmed.startsWith('Tags:') ||
            trimmed.startsWith('TickIntervalMs:') ||
            trimmed.startsWith('IsNpc:') ||
            trimmed.startsWith('Kind:') ||
            trimmed.startsWith('Min:') ||
            trimmed.startsWith('Max:') ||
            trimmed.startsWith('Update:') ||
            trimmed.startsWith('Icon:') ||
            trimmed.startsWith('LocationImage:') ||
            trimmed.startsWith('Location:') ||
            trimmed.startsWith('Starting Inventory:') ||
            trimmed.startsWith('Style:') ||
            trimmed.startsWith('Auto Effects:') ||
            trimmed.startsWith('Option:') ||
            trimmed.startsWith('FinishOption:') ||
            trimmed.startsWith('LeaveOption:') ||
            trimmed.startsWith('Target:') ||
            trimmed.startsWith('Condition:') ||
            trimmed.startsWith('Conditions:') ||
            trimmed.startsWith('Effects:') ||
            trimmed.startsWith('Wait Message:') ||
            trimmed.startsWith('Auto-Transition:') ||
            trimmed.startsWith('Back Option:') ||
            trimmed.startsWith('Variables:') ||
            trimmed.startsWith('Requires Completed:') ||
            trimmed.startsWith('Requires Active:') ||
            trimmed.startsWith('Eligible Characters:') ||
            trimmed.startsWith('Eligible:') ||
            trimmed.startsWith('Start Scene:') ||
            trimmed.startsWith('Scene Prefix:') ||
            trimmed.startsWith('Allowed Parents:') ||
            trimmed.startsWith('Allowed Children:') ||
            trimmed.startsWith('List Order:') ||
            trimmed.startsWith('Hide When Completed:') ||
            trimmed.startsWith('Requires:') ||
            (trimmed.startsWith('Type:') && this.currentSection.toLowerCase() === 'activities') ||
            (trimmed.startsWith('Parent:') && this.currentSection.toLowerCase() === 'activities');
    }
    isVariableDeclaration(line) {
        // Pattern: type name: "description" = value
        const variablePattern = /^\s*\w+\s+\w+:\s*"[^"]*"\s*=\s*.+/;
        return variablePattern.test(line);
    }
    extractSingleLineQuotedContent(line, lineIndex) {
        // Extract content between """ and """
        const startDelim = line.indexOf('"""');
        if (startDelim === -1)
            return null;
        const endDelim = line.indexOf('"""', startDelim + 3);
        if (endDelim === -1) {
            // Error: unclosed delimiter
            return null;
        }
        return line.substring(startDelim + 3, endDelim);
    }
    extractQuotedContentFromCurrentLine(lines, startLineIndex) {
        // Simple approach: find the next """ and take everything in between
        const currentLine = lines[startLineIndex];
        const startDelim = currentLine.indexOf('"""');
        if (startDelim === -1) {
            return { text: null, endIndex: startLineIndex };
        }
        // Extract content from current line after the opening """
        let content = currentLine.substring(startDelim + 3);
        let i = startLineIndex;
        // Look for closing """ on current line first
        const endDelim = content.indexOf('"""');
        if (endDelim !== -1) {
            // Found closing delimiter on the same line
            return { text: content.substring(0, endDelim), endIndex: i };
        }
        // Look for closing """ on subsequent lines
        i++;
        while (i < lines.length) {
            const line = lines[i];
            const endDelim = line.indexOf('"""');
            if (endDelim !== -1) {
                // Found closing delimiter
                content += '\n' + line.substring(0, endDelim);
                return { text: content, endIndex: i };
            }
            else {
                // No closing delimiter on this line, add the line content
                content += '\n' + line;
            }
            i++;
        }
        // No closing delimiter found
        return { text: null, endIndex: startLineIndex };
    }
    extractMultiLineQuotedContent(lines, startIndex) {
        let content = '';
        let j = startIndex;
        while (j < lines.length) {
            if (lines[j].trim() === '"""') {
                // Found closing delimiter
                return { text: content.trim(), endIndex: j };
            }
            if (content !== '') {
                content += '\n';
            }
            content += lines[j];
            j++;
        }
        // Error: unclosed delimiter
        return { text: null, endIndex: j };
    }
    extractSingleLineScriptContent(line, lineIndex) {
        // Extract content between ''' and '''
        const startDelim = line.indexOf("'''");
        if (startDelim === -1)
            return null;
        const endDelim = line.indexOf("'''", startDelim + 3);
        if (endDelim === -1) {
            // Error: unclosed delimiter
            return null;
        }
        return line.substring(startDelim + 3, endDelim);
    }
    extractMultiLineScriptContent(lines, startIndex) {
        let content = '';
        let j = startIndex;
        while (j < lines.length) {
            if (lines[j].trim() === "'''") {
                // Found closing delimiter
                return { text: content.trim(), endIndex: j };
            }
            content += lines[j] + '\n';
            j++;
        }
        // Error: unclosed delimiter
        return { text: null, endIndex: j };
    }
    extractInlineMultiLineScriptContent(lines, lineIndex, restOfLine) {
        // Handle case like: Auto Effects: '''addItem("key");
        //                                   addStat("found");'''
        // restOfLine starts with '''
        const startContent = restOfLine.substring(3); // Remove opening '''
        let content = startContent;
        // Look for closing ''' on current line first
        const closeIndex = content.indexOf("'''");
        if (closeIndex !== -1) {
            // Closing delimiter on same line
            return { text: content.substring(0, closeIndex), endIndex: lineIndex };
        }
        // Look for closing ''' on subsequent lines
        let j = lineIndex + 1;
        while (j < lines.length) {
            const line = lines[j];
            const trimmed = line.trim();
            // Check if line ends with '''
            if (trimmed.endsWith("'''")) {
                // Found closing delimiter
                const lineContent = trimmed.substring(0, trimmed.length - 3);
                if (lineContent) {
                    content += '\n' + lineContent;
                }
                return { text: content.trim(), endIndex: j };
            }
            // No closing delimiter yet, add line to content
            content += '\n' + line;
            j++;
        }
        // Error: unclosed delimiter
        return { text: null, endIndex: j };
    }
    addToken(type, value) {
        this.tokens.push({
            type,
            value,
            line: this.line,
            column: this.column
        });
    }
}
exports.Tokenizer = Tokenizer;
//# sourceMappingURL=Tokenizer.js.map